import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, query, where, orderBy, onSnapshot, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { InvitationWithId } from '../types/team';

function toInvitation(doc: { id: string; data: () => DocumentData }): InvitationWithId {
  const data = doc.data();
  return {
    id: doc.id,
    email: data.email,
    companyId: data.companyId,
    invitedBy: data.invitedBy,
    role: data.role,
    assignedOffices: data.assignedOffices,
    canSeeAllOffices: data.canSeeAllOffices,
    commissionDataName: data.commissionDataName,
    status: data.status,
    token: data.token,
    createdAt: data.createdAt,
    expiresAt: data.expiresAt,
    revocationPending: data.revocationPending === true,
    postStepsDone: data.postStepsDone === true,
    setupUnfinished: data.setupUnfinished === true,
  };
}

/**
 * WS8b M2 (V5-02): "this setup was granted but never finished".
 *
 * The invitation is `accepted`, so it is out of the in-progress list, but the
 * finalisation did not complete: either a post step threw (`postStepsDone`
 * absent) or the bootstrap session could not be revoked (`postStepsDone` true,
 * obligation still open). Before this the state had no route out for EITHER
 * party once the invitee reloaded their page.
 *
 * M5 (V6-03): both shapes are identified by `setupUnfinished`, not by
 * `postStepsDone` and no longer by `revocationPending`. It is written inside
 * the transaction that flips `enrolment_pending -> accepted`, so `accepted &&
 * postStepsDone !== true` still implies it, and it is cleared only by finalise
 * or by the administrator's explicit dismissal. Keying the row on the
 * obligation instead made an administrator's revoke un-clearable: discharging
 * the obligation would have dropped the row, so round 5 left the obligation set
 * after the revoke and the WS7 sweep would have matched a discharged obligation
 * for ever.
 *
 * Testing `postStepsDone !== true` remains wrong for the same reason as before:
 * it matches every ordinary accepted invitation at an `off` or absent tenant,
 * where neither field has ever existed, i.e. the whole live estate, on a
 * surface that must stay neutral. The server keeps the identical rule in
 * `isUnfinishedAcceptedInvitation`.
 */
export function isUnfinishedInvitationSetup(invitation: InvitationWithId): boolean {
  return invitation.status === 'accepted' && invitation.setupUnfinished === true;
}

/**
 * M5 (V6-03): which press the ONE lever on that row is on.
 *   'end_session' - the obligation is open, so the bootstrap session may still
 *                   be alive and ending it is the security action;
 *   'dismiss'     - the session is already dead and only the reminder is left.
 */
export function unfinishedSetupPress(
  invitation: InvitationWithId,
): 'end_session' | 'dismiss' {
  return invitation.revocationPending === true ? 'end_session' : 'dismiss';
}

export function useInvitations(companyId: string | undefined) {
  const [invitations, setInvitations] = useState<InvitationWithId[]>([]);
  const [unfinished, setUnfinished] = useState<InvitationWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Firestore error code behind `error`, so a consumer can tell a DESIGNED
  // denial from a transient failure. The root /invitations rule denies a hidden
  // or broker-restricted admin by design; for that caller the listener error is
  // the expected outcome, not a fault, so it must not be logged as one and the
  // panel must degrade to "hidden for this account" rather than showing a red
  // "failed to load". `error` keeps its existing string shape for the legacy
  // Team page, which ignores this field.
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      setInvitations([]);
      setUnfinished([]);
      return;
    }

    try {
      // WS8b R8 (V2-05): include PARKED invitations (`enrolment_pending`, the
      // invitee has opened the link and is setting up two-factor). Pinned to
      // 'pending' they vanished from Team Management entirely, so an
      // administrator could not see, chase or cancel a mis-sent invitation that
      // had already been opened, and it was exempt from the expiry sweep too.
      // Equality-in, so no new composite index beyond the existing
      // companyId + status + createdAt one.
      const invitationsQuery = query(
        collection(db, 'invitations'),
        where('companyId', '==', companyId),
        where('status', 'in', ['pending', 'enrolment_pending']),
        orderBy('createdAt', 'desc')
      );

      // WS8b M2 (V5-02) / M5 (V6-03): the UNFINISHED-setup list, a second
      // listener rather than a widened `in`.
      //
      // `setupUnfinished` is written inside the same transaction that flips
      // `enrolment_pending -> accepted`, so `accepted` + `postStepsDone !==
      // true` implies it, and this one equality filter returns exactly the set
      // `isUnfinishedInvitationSetup` describes, with nothing lost. Unlike
      // `revocationPending` it survives the administrator ending the session,
      // which is what lets the obligation be discharged without the row going
      // dark.
      //
      // The alternatives do not work. `postStepsDone != true` cannot be a
      // Firestore filter at all: a `!=` query skips documents where the field
      // is MISSING, which is precisely the failing shape. Adding 'accepted' to
      // the `in` above would stream every historical accepted invitation the
      // tenant has ever issued into the browser, permanently, to surface a set
      // that is empty in steady state. Two equality filters need no composite
      // index; ordering is applied client-side for the same reason.
      const unfinishedQuery = query(
        collection(db, 'invitations'),
        where('companyId', '==', companyId),
        where('setupUnfinished', '==', true)
      );

      const unsubscribe = onSnapshot(
        invitationsQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          setInvitations(snapshot.docs.map(toInvitation));
          setLoading(false);
          setError(null);
          setErrorCode(null);
        },
        (err) => {
          const code = (err as { code?: string })?.code ?? null;
          // permission-denied is a rules decision, not a fault: log it at debug
          // volume only, so a restricted admin's ordinary page load does not
          // fill the console with red on every mount.
          if (code !== 'permission-denied') {
            console.error('Error fetching invitations:', err);
          }
          setErrorCode(code);
          setError('Failed to load invitations');
          setLoading(false);
        }
      );

      const unsubscribeUnfinished = onSnapshot(
        unfinishedQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          setUnfinished(
            snapshot.docs
              .map(toInvitation)
              .filter(isUnfinishedInvitationSetup)
              .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))
          );
        },
        (err) => {
          // Non-fatal: the in-progress list is the primary surface, so a
          // failure here must not blank it. Logged, never silent - except for
          // permission-denied, which is the rules layer working as designed for
          // a hidden or broker-restricted admin.
          if ((err as { code?: string })?.code !== 'permission-denied') {
            console.error('Error fetching unfinished invitation setups:', err);
          }
        }
      );

      return () => {
        unsubscribe();
        unsubscribeUnfinished();
      };
    } catch (err) {
      console.error('Error setting up invitations listener:', err);
      setError('Failed to initialize invitations');
      setLoading(false);
    }
  }, [companyId]);

  return { invitations, unfinishedInvitations: unfinished, loading, error, errorCode };
}
